'use client';

import {
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { cn } from '@/lib/cn';
import { readStoredValue, writeStoredValue } from '@/lib/storage';

type ResizableDetailPanelProps = {
  isOpen: boolean;
  onClose: () => void;
  /**
   * false면 backdrop 클릭과 ESC 키 닫기를 차단한다.
   * 처리 중 데이터 손실을 막을 때 사용. default true.
   */
  canClose?: boolean;
  children: ReactNode;
};

const detailPanelWidthKey = 'agentteams-detail-panel-width';
const fallbackDetailPanelWidth = 720;
const minDetailPanelWidth = 560;
const maxDetailPanelWidth = 1120;
const detailPanelViewportRatio = 0.46;
const detailPanelBackdropReserve = 320;
const keyboardResizeStep = 24;

const getMaxDetailPanelWidth = () => {
  if (typeof window === 'undefined') return maxDetailPanelWidth;
  return Math.min(maxDetailPanelWidth, Math.max(minDetailPanelWidth, window.innerWidth - detailPanelBackdropReserve));
};

const clampDetailPanelWidth = (value: number) => {
  return Math.min(getMaxDetailPanelWidth(), Math.max(minDetailPanelWidth, value));
};

const getDefaultDetailPanelWidth = () => {
  if (typeof window === 'undefined') return fallbackDetailPanelWidth;
  return clampDetailPanelWidth(window.innerWidth * detailPanelViewportRatio);
};

const readStoredDetailPanelWidth = () => {
  // 저장소 차단 환경에서는 화면 기본값으로 동작한다 (WEB-R).
  const rawStored = readStoredValue(detailPanelWidthKey);
  if (rawStored === null) {
    return getDefaultDetailPanelWidth();
  }
  const stored = Number(rawStored);
  if (!Number.isFinite(stored)) {
    return getDefaultDetailPanelWidth();
  }
  return clampDetailPanelWidth(stored);
};

export const ResizableDetailPanel = ({ isOpen, onClose, canClose = true, children }: ResizableDetailPanelProps) => {
  const [panelWidth, setPanelWidth] = useState(fallbackDetailPanelWidth);
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    setPanelWidth(readStoredDetailPanelWidth());
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      if (canClose) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, canClose, onClose]);

  const handleBackdropClick = useCallback(() => {
    if (!canClose) return;
    onClose();
  }, [canClose, onClose]);

  const updatePanelWidth = useCallback((nextWidth: number) => {
    const clampedWidth = clampDetailPanelWidth(nextWidth);
    setPanelWidth(clampedWidth);
    writeStoredValue(detailPanelWidthKey, String(clampedWidth));
  }, []);

  const handleResizeStart = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();

      const startX = event.clientX;
      const startWidth = panelWidth;
      const previousCursor = document.body.style.cursor;
      const previousUserSelect = document.body.style.userSelect;
      setIsResizing(true);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      const handlePointerMove = (moveEvent: PointerEvent) => {
        updatePanelWidth(startWidth + startX - moveEvent.clientX);
      };

      const handlePointerUp = () => {
        setIsResizing(false);
        document.body.style.cursor = previousCursor;
        document.body.style.userSelect = previousUserSelect;
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);
        window.removeEventListener('pointercancel', handlePointerUp);
      };

      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
      window.addEventListener('pointercancel', handlePointerUp);
    },
    [panelWidth, updatePanelWidth],
  );

  const handleResizeKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLButtonElement>) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const direction = event.key === 'ArrowLeft' ? 1 : -1;
      updatePanelWidth(panelWidth + direction * keyboardResizeStep);
    },
    [panelWidth, updatePanelWidth],
  );

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 z-50 bg-black/20 backdrop-blur-xs transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      <div
        style={{ '--detail-panel-width': `${panelWidth}px` } as CSSProperties}
        className={cn(
          'fixed inset-0 z-50 flex h-[100dvh] max-h-[100dvh] w-[100dvw] max-w-[100dvw] flex-col overflow-hidden bg-base-100 pb-[env(safe-area-inset-bottom)] ease-in-out lg:inset-y-0 lg:left-auto lg:right-0 lg:h-auto lg:max-h-none lg:w-[var(--detail-panel-width)] lg:max-w-none lg:pb-0',
          // Tailwind 4의 translate-x-* 는 transform이 아니라 `translate` 속성을 쓴다. 따라서 슬라이드를
          // 전환하려면 transition에 translate가 포함돼야 한다(기존 transition-[transform,width]는 translate를
          // 안 잡아 v4에서 슬라이드가 즉시 점프했다). transition-transform(v4)은 translate를 포함한다.
          isResizing ? 'transition-transform duration-300' : 'transition-[translate,width] duration-300',
          isOpen ? 'translate-x-0 shadow-[-24px_0_40px_-20px_rgba(0,0,0,0.55)]' : 'translate-x-full',
        )}
      >
        {isOpen ? (
          <button
            type="button"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize detail panel"
            aria-valuemin={minDetailPanelWidth}
            aria-valuemax={getMaxDetailPanelWidth()}
            aria-valuenow={panelWidth}
            onPointerDown={handleResizeStart}
            onKeyDown={handleResizeKeyDown}
            className="group absolute -left-1.5 top-0 z-10 hidden h-full w-3 cursor-col-resize items-center justify-center outline-none lg:flex"
          >
            <span className="h-6 w-0.5 rounded-full bg-[var(--color-border)] transition group-hover:h-8 group-hover:bg-[var(--color-primary)] group-focus-visible:h-8 group-focus-visible:bg-[var(--color-focus-ring)]" />
          </button>
        ) : null}
        {children}
      </div>
    </>
  );
};
